import * as R from 'ramda';
import { get } from 'utils/ajax';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  MediaItem,
  ListResponse,
  LiveVideoItem,
  ILoadMoreResult,
} from './video-inteface';
import { transformSnapshotPath } from './v2-video-util';

const defaultPagination = {
  page: 1,
  page_size: 20,
};

const batchLoadList = (ids: Array<number>, name = '') => {
  //  /api/v2/gallerymedium/batch
  return new Promise<Array<LiveVideoItem>>((resolve) => {
    const nameParam = name ? `&desc=${name}` : '';
    get(`/api/v2/gallerymedium/batch?ids=${ids.join(',')}${nameParam}`).then((res) => {
      if (res.code === 200) {
        resolve(res.data);
      }
    });
  });
};

export const getRandomDomain = (min: number, max: number) => {
  //  //image{1}.jimage.cn
  return `//image${Math.floor(
    Math.random() * (max - min + 1) + min
  )}.jimage.cn/`;
};

export const transformLiveVideoItem = (item: LiveVideoItem): MediaItem => {
  //  thumb => attachment/1eaf091a0796e8e7721289218628cfee.jpg!wx_gallery_mode_2
  const thumb = item.thumb;
  const path = getRandomDomain(1, 6) + thumb + '!wx_gallery_mode_2';
  //  play_back_url
  const videoUrl = item.source_detail.cut_play_url;
  const name = item.desc;
  const desc = '';
  return {
    path,
    videoUrl,
    name,
    desc,
  } as unknown as MediaItem;
};

export const useV2Video = (props: any) => {
  const { selectVideoSource = [], videos = [], name = '' } = props;

  const [channelList, setChannelList] = useState<Array<number>>([]);
  const [tag_id, setTag_id] = useState(0);
  const [currentVideos, setCurrentVideos] = useState<MediaItem[]>([]);
  const [requestParam, setRequestParam] = useState<Record<string, any>>({});
  const [ready, setReady] = useState(false);
  const [pagination, setPagination] = useState(defaultPagination);
  const [total, setTotal] = useState(0);

  const hasMoreRef = useRef(true);

  const trimmedName = useMemo(() => name.trim(), [name]);


  useEffect(()=>{
    const lastSource = R.pipe(
        R.last,
        R.defaultTo('live')
    )(selectVideoSource) as string;
    if(lastSource === 'live'){
      batchLoadList(videos as unknown as Array<number>, name).then((res) => {
        setCurrentVideos(res.map(transformLiveVideoItem));
      });
      setTotal(videos.length);
      hasMoreRef.current = false;
    }
  },[selectVideoSource, videos, name])


  useEffect(() => {
    const lastSource = R.pipe(
      R.last,
      R.defaultTo('live')
    )(selectVideoSource) as string;
    if (lastSource !== 'live') {
      const [channelStr, tagIdStr] = lastSource.split('-');
      if (channelStr) {
        const channelStrArray = channelStr.split(',');
        const channels = channelStrArray.map((channel) =>
          parseInt(channel, 10)
        );
        setChannelList(channels);
      }
      if (tagIdStr) {
        const tagId = parseInt(tagIdStr, 10);
        setTag_id(tagId);
      } else {
        // 只选择视频分类时代表该分类全部视频，不沿用上次的标签。
        setTag_id(0);
      }
      setCurrentVideos([]);
      setPagination(defaultPagination);
      hasMoreRef.current = true;
    }
  }, [selectVideoSource, videos]);

  useEffect(() => {
    if (!selectVideoSource.includes('live') && channelList.length === 0) {
      return;
    }
    if (channelList.every((channel) => channel < 0)) {
      return;
    }
    setRequestParam({
      channel: channelList.every((channel) => channel === 0)
        ? undefined
        : channelList.join(','),
      tag_id: tag_id > 0 ? tag_id : undefined,
      name: trimmedName ? trimmedName : undefined,
    });
    setReady(true);
  }, [channelList, tag_id, trimmedName, selectVideoSource]);

  useEffect(() => {
    if (!ready || selectVideoSource.includes('live')) {
      return;
    }

    const paramsWithoutUndefinedValue = R.pipe(
      R.toPairs,
      R.filter(([_, value]) => value !== undefined),
      R.fromPairs
    )(requestParam);

    get(`/api/v2/video/${window.gallery.orderid}/list`, {
      type: 'video',
      ...paramsWithoutUndefinedValue,
      ...pagination,
      hidden: 0,
    }).then((res: ListResponse<MediaItem>) => {
      if (res.code === 200) {
        setCurrentVideos((prevVideos) => {
          const list = [
            ...prevVideos,
            ...(res.data.items || []).map(transformSnapshotPath),
          ];
          hasMoreRef.current = list.length < res.data.total;
          return list;
        });
        setTotal(res.data.total);
      }
    });
  }, [requestParam, ready, pagination, selectVideoSource]);

  const loadMoreVideos = (): Promise<ILoadMoreResult> => {
    return new Promise((resolve) => {
      if (selectVideoSource.includes('live')) {
        resolve({ nomore: true, message: '没有数据了' });
        return;
      }

      setTimeout(() => {
        if (hasMoreRef.current) {
          setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
          resolve({ nomore: false, message: '' });
        } else {
          resolve({ nomore: true, message: '没有数据了' });
        }
      }, 0);
    });
  };

  const resetList = () => {
    setCurrentVideos([]);
    setPagination(defaultPagination);
    hasMoreRef.current = true;
  };

  return { currentVideos, loadMoreVideos, total, resetList };
};
